/**
 * Turn lifecycle for useChatApp: sending typed/voice text, the chat payload,
 * and approving confirmation cards. Split out of useChatApp.js unchanged
 * (2026-09-25 gap closure).
 */
import { loadSessions, createSession, hasSessionUser } from '../../utils/chatSessions';
import {
  findConfirmationCard,
  markCardSubmitting,
  resolveRenderCardKind,
  revertCardToPending,
  staleAllPendingCards,
  staleCardIfPending,
} from '../../utils/confirmationCards';
import { CARD_KIND_INCIDENT_REPORT } from '../../utils/confirmationRequest';
import { nextMessageId } from '../../utils/messageIds';
import { buildReportDraftPrompt } from '../../utils/reportDraft';
import {
  MY_ZIP_RE,
  NEAR_ME_BUFFER_METERS,
  NEAR_ME_RE,
  MESSAGE_TOO_LONG_MESSAGE,
  GUARDIAN_MAX_MESSAGE_CHARS,
  boundedHistory,
  extractZipCode,
  messageTooLongForFrame,
} from './chatConstants';

/**
 * @param {object} s - Context from useChatState.
 * @param {object} opts - useChatApp options.
 * @param {{ promptNearMeLocation: Function }} location
 */
export function useChatTurn(s, {
  userId, authToken, guardianUser, scopeProfile, requiresGuardianAuth, openAuthModal,
  plainLanguage = false,
}, { promptNearMeLocation }) {
  const {
    setSessions, setSessionId, messages, setMessages, inputMessage, setInputMessage,
    isConnected, setIsLoading, inFlightRef, endTurn, ws, clientLocation, sessionPlace,
    setSessionPlace, expectAwosReplyRef, lastAssistantTextRef, submittingConfirmationIdRef,
    wsRef, sessionIdRef, sendMessageRef, startTurnWatchdogRef, draftRef,
  } = s;

  /**
   * Sends the composer text (or override) to the agent over WebSocket.
   * @param {string} [overrideText] - Optional text from suggestion chips or STT.
   * @param {{ awosReply?: boolean }} [options] - Speak an AWOS reply only for mic prompts.
   * @returns {'empty'|'busy'|'too-long'|'offline'|'auth-required'|'no-geolocation'|'sent'} 'sent'
   *   means the turn started or is waiting on the location prompt. Voice uses it
   *   to keep unsent words (2026-09-25 gap closure).
   */
  const sendMessage = (overrideText, options = {}) => {
    const text = (overrideText ?? inputMessage).trim();
    if (!text) return 'empty';
    if (inFlightRef.current) return 'busy';
    // Refused before any bubble, prompt, or send; the draft stays in the box
    // (2026-09-25 adversarial pass 15: an over-size frame closes the socket).
    if (
      messageTooLongForFrame(text, authToken)
      || (scopeProfile === 'guardian' && text.length > GUARDIAN_MAX_MESSAGE_CHARS)
    ) {
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'error',
          content: MESSAGE_TOO_LONG_MESSAGE,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      return 'too-long';
    }
    expectAwosReplyRef.current = Boolean(options.awosReply);

    if (!ws || !isConnected) {
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'error',
          content: 'Not connected to server. Please check your connection and try again.',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      return 'offline';
    }

    if (requiresGuardianAuth) {
      openAuthModal();
      return 'auth-required';
    }

    // Near-me without a saved location: show the browser permission popup, then send.
    if (NEAR_ME_RE.test(text) && !clientLocation) {
      return promptNearMeLocation(text, dispatchNearMeMessage);
    }

    return dispatchNearMeMessage(text, clientLocation);
  };
  sendMessageRef.current = sendMessage;

  /**
   * Builds the WebSocket chat payload shared by every send path (typed/voice
   * text via dispatchNearMeMessage, and card approval via confirmReport).
   * `confirmationId` is only ever set by confirmReport() — typed text must
   * never carry it, per the "typed text never approves" contract.
   * @param {string|null} activeSessionId
   * @param {string} outboundText
   * @param {Array} priorMessages - Thread messages to render as `history`.
   * @param {string|null} [confirmationId]
   * @param {object} [metadataExtra] - Extra fields merged into `metadata`.
   */
  const buildChatPayload = (
    activeSessionId,
    outboundText,
    priorMessages,
    confirmationId = null,
    metadataExtra = {},
  ) => {
    // Bounded to what the agent accepts (2026-09-25 adversarial pass 1), in
    // the room the rest of the frame leaves (adversarial pass 5).
    const payload = {
      session_id: activeSessionId,
      message: outboundText,
      token: authToken,
      scope_profile: scopeProfile,
      history: [],
      metadata: {
        guardian_user_id: guardianUser?.id || null,
        // The user's explicit plain-language toggle (D2 §3.3): authoritative on
        // every turn so the agent runs the deterministic plain-mode post-check.
        plain_language: Boolean(plainLanguage),
        ...metadataExtra,
      },
      ...(confirmationId ? { confirmation_id: confirmationId } : {}),
    };
    payload.history = boundedHistory(priorMessages, JSON.stringify(payload).length);
    return payload;
  };

  /**
   * Persist session if needed and send a chat turn (optionally with client location).
   * @param {string} text - User-visible message text.
   * @param {{lat:number,lon:number,accuracy_m?:number,placeName?:string}|null} loc
   */
  const dispatchNearMeMessage = (text, loc, preAddedId = null) => {
    // First message after sign-in: ensure we have a persisted session for this user.
    let activeSessionId = sessionIdRef.current;
    if (hasSessionUser(userId) && !activeSessionId) {
      const session = createSession(userId);
      activeSessionId = session.id;
      setSessionId(session.id);
      setSessions(loadSessions(userId));
      sessionIdRef.current = session.id;
    }

    // Remember any zip named this turn (e.g. user replies "60102").
    const zipFromText = extractZipCode(text);
    let placeForTurn = sessionPlace;
    if (zipFromText) {
      placeForTurn = { type: 'zip', value: zipFromText, label: zipFromText };
      setSessionPlace(placeForTurn);
    }

    let outboundText = text;
    // "my zip code" → reuse the zip already established in this thread.
    if (MY_ZIP_RE.test(text) && placeForTurn?.value) {
      outboundText = `${text}\n\n[session_place type=${placeForTurn.type} value=${placeForTurn.value}; `
        + `this is the user's zip/place for this chat — geocode it and answer; do not ask again]`;
    } else if (loc && NEAR_ME_RE.test(text)) {
      const place = loc.placeName ? ` (${loc.placeName})` : '';
      outboundText = `${text}\n\n[client_location lat=${loc.lat} lon=${loc.lon} `
        + `accuracy_m=${loc.accuracy_m ?? 'unknown'}${place}; `
        + `use ${NEAR_ME_BUFFER_METERS}m (50 km) radius; report fire distances from these coordinates]`;
    } else if (zipFromText) {
      outboundText = `${text}\n\n[session_place type=zip value=${zipFromText}; `
        + `use this zip for location tools]`;
    }

    // Avoid duplicating the user bubble when we already added it before geolocation.
    // Any typed/voice send invalidates a still-pending confirmation card —
    // the agent does the same server-side (see utils/confirmationCards.js).
    setMessages((prev) => {
      const staled = staleAllPendingCards(prev);
      // Match the bubble added before the location prompt by id: other
      // messages may have landed after it (2026-09-25 pass 4 C2).
      if (preAddedId !== null && staled.some((m) => m.id === preAddedId)) {
        return staled;
      }
      const last = staled[staled.length - 1];
      if (preAddedId === null && last?.type === 'user' && last.content === text) {
        return staled;
      }
      return [
        ...staled,
        {
          id: nextMessageId(),
          type: 'user',
          content: text,
          timestamp: new Date().toLocaleTimeString(),
        },
      ];
    });
    setInputMessage('');
    lastAssistantTextRef.current = '';
    inFlightRef.current = true;
    setIsLoading(true);

    // Live socket, not the render closure: the geolocation callback runs
    // after the prompt closes and may outlive the socket it started on.
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      endTurn();
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'error',
          content: 'Not connected to server. Please check your connection and try again.',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      return 'offline';
    }

    try {
      // Prior turns only — agent hydrates empty memory from this on reconnect.
      let prior = messages.filter(
        (m) => (m.type === 'user' || m.type === 'assistant') && typeof m.content === 'string',
      );
      if (
        prior.length > 0
        && prior[prior.length - 1].type === 'user'
        && prior[prior.length - 1].content === text
      ) {
        prior = prior.slice(0, -1);
      }
      const metadataExtra = {
        ...(placeForTurn
          ? {
              session_place: {
                type: placeForTurn.type,
                value: placeForTurn.value,
                label: placeForTurn.label || placeForTurn.value,
              },
            }
          : {}),
        ...(loc && NEAR_ME_RE.test(text)
          ? {
              client_location: {
                lat: loc.lat,
                lon: loc.lon,
                accuracy_m: loc.accuracy_m,
                place_name: loc.placeName || null,
                buffer_meters: NEAR_ME_BUFFER_METERS,
              },
            }
          : {}),
      };
      const payload = buildChatPayload(activeSessionId, outboundText, prior, null, metadataExtra);

      socket.send(JSON.stringify(payload));
      // No `done` within the bound ends the turn (2026-09-25 adversarial pass 1).
      if (startTurnWatchdogRef.current) startTurnWatchdogRef.current(socket);
      return 'sent';
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'error',
          content: 'Failed to send message. Please try again.',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      endTurn();
      return 'offline';
    }
  };

  /**
   * Approves a pending confirmation card: sends the fixed "Confirm" text plus
   * `confirmation_id` so the agent executes the report it already has staged.
   * This is the only send path that ever sets `confirmation_id` — typed text
   * (dispatchNearMeMessage above) never does.
   * @param {string} confirmationId - The `id` from the confirmation_request event.
   */
  const confirmReport = (confirmationId) => {
    if (inFlightRef.current) return;
    const card = findConfirmationCard(messages, confirmationId);
    if (!card || card.state !== 'pending') return;
    // Only a card this client can display is ever approved: an unsupported
    // kind, or a card whose fields fail the incident rules, is refused even
    // if its state says pending (plan 05 B3, 2026-09-29).
    if (resolveRenderCardKind(card) !== CARD_KIND_INCIDENT_REPORT) return;
    // Re-check expiry at tap time: the expiry timer is throttled in background
    // tabs and on sleeping phones (2026-09-25 adversarial pass 1).
    if (typeof card.expiresAt === 'number' && Date.now() >= card.expiresAt) {
      setMessages((prev) => staleCardIfPending(prev, confirmationId));
      return;
    }

    if (requiresGuardianAuth) {
      openAuthModal();
      return;
    }

    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'error',
          content: 'Not connected to server. Please check your connection and try again.',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      return;
    }

    let activeSessionId = sessionIdRef.current;
    if (hasSessionUser(userId) && !activeSessionId) {
      const session = createSession(userId);
      activeSessionId = session.id;
      setSessionId(session.id);
      setSessions(loadSessions(userId));
      sessionIdRef.current = session.id;
    }

    // Optimistic: 'submitting' now; confirming is itself a new send, so any
    // other still-pending card goes stale in the same pass.
    setMessages((prev) => markCardSubmitting(prev, confirmationId));
    submittingConfirmationIdRef.current = confirmationId;
    expectAwosReplyRef.current = false;
    lastAssistantTextRef.current = '';
    inFlightRef.current = true;
    setIsLoading(true);

    try {
      const prior = messages.filter(
        (m) => (m.type === 'user' || m.type === 'assistant') && typeof m.content === 'string',
      );
      const payload = buildChatPayload(activeSessionId, 'Confirm', prior, confirmationId);
      socket.send(JSON.stringify(payload));
      if (startTurnWatchdogRef.current) startTurnWatchdogRef.current(socket);
    } catch (error) {
      console.error('Error confirming report:', error);
      submittingConfirmationIdRef.current = null;
      endTurn();
      setMessages((prev) => revertCardToPending(prev, confirmationId));
    }
  };

  /**
   * "Help me fill this" (plan Track 5 B6, 2026-09-30): asks the agent, on the
   * same socket and session as the chat, to DRAFT report fields from the
   * conversation so far. The turn is invisible in the thread (no user bubble;
   * the reply is captured by useChatSocket via draftRef) and never carries
   * `confirmation_id`, so it can approve nothing. The result only pre-fills the
   * form; the form stays the only submit path.
   * @returns {Promise<{ ok: true, text: string }
   *   | { ok: false, reason: 'busy'|'offline'|'auth-required'|'too-long'|'no-conversation'|'agent-error'|'invalid'|'interrupted' }>}
   */
  const requestReportDraft = () => {
    const hasConversation = messages.some((m) => m.type === 'user' && typeof m.content === 'string' && m.content.trim());
    if (!hasConversation) return Promise.resolve({ ok: false, reason: 'no-conversation' });
    if (inFlightRef.current || draftRef.current) return Promise.resolve({ ok: false, reason: 'busy' });
    if (requiresGuardianAuth) {
      openAuthModal();
      return Promise.resolve({ ok: false, reason: 'auth-required' });
    }
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return Promise.resolve({ ok: false, reason: 'offline' });
    }
    const prompt = buildReportDraftPrompt(clientLocation);
    if (messageTooLongForFrame(prompt, authToken)) {
      return Promise.resolve({ ok: false, reason: 'too-long' });
    }

    return new Promise((resolve) => {
      draftRef.current = { text: '', error: false, overflow: false, resolve };
      // Like any send, this invalidates a still-pending confirmation card
      // server-side; mirror it locally (see utils/confirmationCards.js).
      setMessages((prev) => staleAllPendingCards(prev));
      expectAwosReplyRef.current = false;
      lastAssistantTextRef.current = '';
      inFlightRef.current = true;
      setIsLoading(true);
      try {
        const prior = messages.filter(
          (m) => (m.type === 'user' || m.type === 'assistant') && typeof m.content === 'string',
        );
        const payload = buildChatPayload(sessionIdRef.current, prompt, prior);
        socket.send(JSON.stringify(payload));
        if (startTurnWatchdogRef.current) startTurnWatchdogRef.current(socket);
      } catch (error) {
        console.error('Error requesting report draft:', error);
        // endTurn settles the pending draft as interrupted.
        endTurn();
      }
    });
  };

  /** Enter sends the message; Shift+Enter inserts a newline. */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return { sendMessage, confirmReport, requestReportDraft, handleKeyDown };
}
